import type { fabric } from 'fabric';

const FLOAT_EDITOR_OFFSET_X = 12;
const DELETE_ICON_GAP = 24;
const FLOAT_EDITOR_GAP_FROM_OBJECT = FLOAT_EDITOR_OFFSET_X + DELETE_ICON_GAP;
const FLOAT_EDITOR_OFFSET_Y = 0;
const FLOAT_EDITOR_WIDTH = 312;
const FLOAT_EDITOR_MIN_HEIGHT = 250;
const WINDOW_PADDING = 8;
const GAP_FROM_FIGURE = 8;
const CANVAS_PADDING = 16;

export type ViewportRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export function getObjectViewportRect(
  canvas: fabric.Canvas,
  object: fabric.Object
): ViewportRect | null {
  const canvasEl = canvas.getElement();
  if (!canvasEl) return null;

  (object as fabric.Object & { setCoords?: () => void }).setCoords?.();

  const objWithCoords = object as fabric.Object & {
    getCoords: (a?: boolean, b?: boolean) => Array<{ x: number; y: number }>;
  };
  const coords = objWithCoords.getCoords?.(false, true);
  if (!coords || coords.length < 4) return null;

  const canvasRect = canvasEl.getBoundingClientRect();
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const p of coords) {
    const x = canvasRect.left + p.x;
    const y = canvasRect.top + p.y;
    left = Math.min(left, x);
    top = Math.min(top, y);
    right = Math.max(right, x);
    bottom = Math.max(bottom, y);
  }
  return { left, top, right, bottom };
}

export default function getObjectViewportPosition(
  canvas: fabric.Canvas,
  object: fabric.Object
): { left: number; top: number } | null {
  const rect = getObjectViewportRect(canvas, object);
  if (!rect) return null;
  return {
    left: rect.right + FLOAT_EDITOR_GAP_FROM_OBJECT,
    top: rect.top + FLOAT_EDITOR_OFFSET_Y
  };
}

export function constrainPositionToWindow(
  pos: { left: number; top: number } | null,
  options?: {
    canvas?: fabric.Canvas | null;
    width?: number;
    height?: number;
  }
) {
  if (!pos) return pos;
  const width = options?.width ?? FLOAT_EDITOR_WIDTH;
  const height = options?.height ?? FLOAT_EDITOR_MIN_HEIGHT;

  const canvasRect = options?.canvas?.getElement()?.getBoundingClientRect();
  const minLeft = canvasRect
    ? canvasRect.left + CANVAS_PADDING
    : WINDOW_PADDING;
  const minTop = canvasRect ? canvasRect.top + CANVAS_PADDING : WINDOW_PADDING;
  const maxLeft = canvasRect
    ? canvasRect.right - width - CANVAS_PADDING
    : window.innerWidth - width - WINDOW_PADDING;
  const maxTop = canvasRect
    ? canvasRect.bottom - height - CANVAS_PADDING
    : window.innerHeight - height - WINDOW_PADDING;

  const boundedLeft = maxLeft < minLeft ? minLeft : maxLeft;
  const boundedTop = maxTop < minTop ? minTop : maxTop;

  return {
    left: Math.max(minLeft, Math.min(pos.left, boundedLeft)),
    top: Math.max(minTop, Math.min(pos.top, boundedTop))
  };
}

function rectsOverlap(a: ViewportRect, b: ViewportRect): boolean {
  return !(
    a.right <= b.left ||
    b.right <= a.left ||
    a.bottom <= b.top ||
    a.top >= b.bottom
  );
}

function getFormRect(pos: { left: number; top: number }): ViewportRect {
  return {
    left: pos.left,
    top: pos.top,
    right: pos.left + FLOAT_EDITOR_WIDTH,
    bottom: pos.top + FLOAT_EDITOR_MIN_HEIGHT
  };
}

export function choosePosition(
  canvas: fabric.Canvas,
  objectRect: ViewportRect,
  initialPos: { left: number; top: number }
): { left: number; top: number } | null {
  const formRectAtInitial = getFormRect(initialPos);
  if (!rectsOverlap(formRectAtInitial, objectRect)) {
    return initialPos;
  }

  const candidates: Array<{ left: number; top: number }> = [
    {
      left: objectRect.left - FLOAT_EDITOR_WIDTH - GAP_FROM_FIGURE,
      top: initialPos.top
    },
    {
      left: objectRect.right + FLOAT_EDITOR_GAP_FROM_OBJECT,
      top: initialPos.top
    },
    {
      left: initialPos.left,
      top: objectRect.top - FLOAT_EDITOR_MIN_HEIGHT - GAP_FROM_FIGURE
    },
    { left: initialPos.left, top: objectRect.bottom + GAP_FROM_FIGURE }
  ];

  for (const pos of candidates) {
    const constrained = constrainPositionToWindow(pos, { canvas });
    if (constrained) {
      const formRect = getFormRect(constrained);
      if (!rectsOverlap(formRect, objectRect)) {
        return constrained;
      }
    }
  }

  const canvasEl = canvas.getElement();
  const canvasRect = canvasEl?.getBoundingClientRect();
  if (canvasRect) {
    return constrainPositionToWindow(
      {
        left: canvasRect.left + CANVAS_PADDING,
        top: canvasRect.top + CANVAS_PADDING
      },
      { canvas }
    );
  }

  return constrainPositionToWindow(initialPos, { canvas });
}
